import { type LucideIcon } from 'lucide-react';
import { type ReactNode } from 'react';

import { BrandMark } from '@/app/layout/brand-mark';
import { Spinner } from '@/components/ui/spinner';

/** Full-screen splash shown while the session/profile resolves (never the private shell). */
export function AuthSplash({ label = 'Cargando sesión…' }: { label?: string }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-5 bg-background">
      <BrandMark />
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner label={label} />
        <span aria-hidden="true">{label}</span>
      </div>
    </div>
  );
}

interface FullPageStateProps {
  icon: LucideIcon;
  title: string;
  description: ReactNode;
  actions?: ReactNode;
  tone?: 'neutral' | 'danger';
}

export function FullPageState({
  icon: Icon,
  title,
  description,
  actions,
  tone = 'neutral',
}: FullPageStateProps) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div
        role={tone === 'danger' ? 'alert' : undefined}
        className="w-full max-w-md rounded-lg border border-border bg-surface p-8 text-center shadow-card"
      >
        <div
          className={
            tone === 'danger'
              ? 'mx-auto mb-4 flex size-11 items-center justify-center rounded-full bg-danger-soft text-danger'
              : 'mx-auto mb-4 flex size-11 items-center justify-center rounded-full bg-surface-sunken text-muted-foreground'
          }
        >
          <Icon className="size-5" aria-hidden="true" />
        </div>
        <h1 className="text-base font-semibold text-foreground">{title}</h1>
        <div className="mt-1.5 text-sm text-pretty text-muted-foreground">{description}</div>
        {actions && <div className="mt-6 flex flex-wrap justify-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}
